import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Alert,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CircularProgress from '@mui/material/CircularProgress';
import {
  validateEmployeeMasterCsv,
  submitEmployeeMasterCsv,
  exportEmployeeMasterCsv,
} from '../../../admin/Services/EmployeeMasterCsvService';
import { SHOW_NOTIFICATION } from '../../../constants';
import EmployeeMasterCsvExportDrawer from './EmployeeMasterCsvExportDrawer';
import '../BulkUpload/BulkUpload.css';

/**
 * Employee Master CSV — update-only import/export.
 * Separate from Workday Bulk Upload.
 */
export default function EmployeeMasterCsvComponent() {
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportDrawerOpen, setExportDrawerOpen] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [submissionResult, setSubmissionResult] = useState(null);

  const showNotification = (type, message) => {
    dispatch({
      type: SHOW_NOTIFICATION,
      data: { open: true, type, message },
    });
  };

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (file.name.endsWith('.csv')) {
        setSelectedFile(file);
        setValidationResult(null);
        setSubmissionResult(null);
      } else {
        showNotification('error', 'Only CSV files are supported.');
      }
    }
  };

  const handleDragOver = (event) => event.preventDefault();

  const handleDrop = (event) => {
    event.preventDefault();
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      const file = event.dataTransfer.files[0];
      if (file.name.endsWith('.csv')) {
        setSelectedFile(file);
        setValidationResult(null);
        setSubmissionResult(null);
      } else {
        showNotification('error', 'Only CSV files are supported.');
      }
    }
  };

  const handleValidate = async () => {
    if (!selectedFile) return;
    setIsValidating(true);
    setValidationResult(null);
    setSubmissionResult(null);
    const response = await validateEmployeeMasterCsv(selectedFile);
    setIsValidating(false);
    if (response && response.isError === false) {
      const validationData = response.data?.data;
      if (validationData) {
        setValidationResult(validationData);
        if (validationData.allPassed) {
          showNotification('success', 'CSV validation successful! All records are valid.');
        } else {
          showNotification('warning', 'CSV validation found errors in some records.');
        }
      } else {
        showNotification('error', 'Unexpected response format from server.');
      }
    } else {
      showNotification('error', response?.message || 'Failed to validate CSV file.');
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) return;
    setIsSubmitting(true);
    const response = await submitEmployeeMasterCsv(selectedFile);
    setIsSubmitting(false);
    if (response && response.isError === false) {
      const submitData = response.data?.data;
      if (submitData) {
        setSubmissionResult(submitData);
        showNotification('success', submitData.message || 'Employee master CSV import complete.');
        setSelectedFile(null);
        setValidationResult(null);
      } else {
        showNotification('error', 'Unexpected response format from server.');
      }
    } else {
      showNotification('error', response?.message || 'Failed to submit employee master CSV.');
    }
  };

  const handleExport = async (filters) => {
    setIsExporting(true);
    const response = await exportEmployeeMasterCsv(filters || {});
    setIsExporting(false);
    if (response && response.isError === false && response.data) {
      // Defensive: header-only CSV means no matching employees (e.g. older backend).
      const csvText = typeof response.data.text === 'function'
        ? await response.data.text()
        : String(response.data);
      const dataRows = csvText
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
      if (dataRows.length <= 1) {
        showNotification(
          'warning',
          'No employees matched the selected filters.',
        );
        return;
      }
      const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employee-master.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showNotification('success', 'Employee master CSV downloaded.');
      setExportDrawerOpen(false);
    } else {
      const message = response?.message || 'Failed to export employee CSV.';
      const isEmptyResult = /no employees matched/i.test(message);
      showNotification(isEmptyResult ? 'warning' : 'error', message);
    }
  };

  const downloadReport = () => {
    if (!validationResult || !validationResult.validationReportCsv) return;
    try {
      const byteCharacters = atob(validationResult.validationReportCsv);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `employee_master_validation_${selectedFile?.name || 'report'}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      showNotification('error', 'Error generating report download: ' + error.message);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setValidationResult(null);
    setSubmissionResult(null);
  };

  return (
    <Box className="bulk-upload-container">
      <Paper className="bulk-upload-header-card" elevation={3}>
        <Typography variant="h4" className="bulk-upload-title">
          Employee Master CSV
        </Typography>
        <Typography variant="body1" className="bulk-upload-subtitle">
          Update existing employees via CSV (not create). Matching requires both Employee Code and Email.
          Dates use dd/MM/yyyy. Blank cells preserve existing values. Leave Group Name blank = preserve;
          use &quot;No group&quot; to clear. Job type transitions are not supported in CSV v1.
        </Typography>
        {validationResult?.columnHelp && (
          <Typography variant="body2" sx={{ mt: 1, color: '#64748b' }}>
            {validationResult.columnHelp}
          </Typography>
        )}
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setExportDrawerOpen(true)}
            disabled={isExporting}
          >
            Download Employee CSV
          </Button>
          {isExporting && (
            <Button variant="text" disabled startIcon={<CircularProgress size={18} />}>
              Exporting...
            </Button>
          )}
        </Box>
      </Paper>

      <EmployeeMasterCsvExportDrawer
        open={exportDrawerOpen}
        onClose={() => setExportDrawerOpen(false)}
        onDownload={handleExport}
        isExporting={isExporting}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={validationResult ? 6 : 12}>
          <Paper className="upload-section-card" elevation={2}>
            <Typography variant="h6" className="card-section-title">
              1. Choose CSV File
            </Typography>
            <div
              className={`dropzone-area ${selectedFile ? 'has-file' : ''}`}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="employee-master-csv-input"
                accept=".csv"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="employee-master-csv-input" className="dropzone-label">
                <CloudUploadIcon className="upload-icon" />
                {selectedFile ? (
                  <Box className="file-info-box">
                    <Typography className="selected-filename">{selectedFile.name}</Typography>
                    <Typography className="selected-filesize">
                      ({(selectedFile.size / 1024).toFixed(2)} KB)
                    </Typography>
                  </Box>
                ) : (
                  <Typography className="dropzone-text">
                    Drag and drop your CSV file here, or <span>Browse</span>
                  </Typography>
                )}
              </label>
            </div>

            {selectedFile && (
              <Box className="action-buttons-box">
                <Button
                  variant="contained"
                  onClick={handleValidate}
                  disabled={isValidating || isSubmitting}
                  className="validate-btn"
                  startIcon={
                    isValidating ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                >
                  {isValidating ? 'Validating...' : 'Validate CSV'}
                </Button>

                {validationResult && validationResult.allPassed && (
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="submit-btn"
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <CheckCircleOutlineIcon />
                      )
                    }
                  >
                    {isSubmitting ? 'Applying...' : 'Apply Updates'}
                  </Button>
                )}
              </Box>
            )}
          </Paper>

          {submissionResult && (
            <Paper className="results-alert-card" elevation={2}>
              <Alert
                severity={submissionResult.failureCount > 0 ? 'warning' : 'success'}
                icon={<CheckCircleOutlineIcon fontSize="inherit" />}
              >
                <Typography variant="h6">Import Complete</Typography>
                <Typography variant="body2">
                  Success: <strong>{submissionResult.successCount}</strong>
                  {submissionResult.failureCount > 0 && (
                    <>
                      {' '}
                      | Failed: <strong>{submissionResult.failureCount}</strong>
                    </>
                  )}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {submissionResult.message}
                </Typography>
              </Alert>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button variant="outlined" onClick={handleReset}>
                  Upload Another File
                </Button>
              </Box>
            </Paper>
          )}
        </Grid>

        {validationResult && (
          <Grid item xs={12} md={6}>
            <Paper className="validation-report-card" elevation={2}>
              <Typography variant="h6" className="card-section-title">
                2. Validation Report
              </Typography>
              <Box className="metrics-box">
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Box className="metric-card total">
                      <Typography className="metric-value">
                        {validationResult.totalRows}
                      </Typography>
                      <Typography className="metric-label">Total Rows</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box className="metric-card passed">
                      <Typography className="metric-value">
                        {validationResult.passCount}
                      </Typography>
                      <Typography className="metric-label">Passed</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box
                      className={`metric-card ${
                        validationResult.failCount > 0 ? 'failed' : 'no-failed'
                      }`}
                    >
                      <Typography className="metric-value">
                        {validationResult.failCount}
                      </Typography>
                      <Typography className="metric-label">Failed</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {validationResult.allPassed ? (
                <Box className="status-banner success">
                  <CheckCircleOutlineIcon className="status-icon" />
                  <Typography>
                    CSV is fully valid. Click &quot;Apply Updates&quot; to save changes.
                  </Typography>
                </Box>
              ) : (
                <Box className="status-banner error">
                  <ErrorOutlineIcon className="status-icon" />
                  <Typography>
                    Validation failed for {validationResult.failCount} rows. Please
                    correct and retry.
                  </Typography>
                </Box>
              )}

              {validationResult.failCount > 0 && (
                <Box className="error-actions-box">
                  <Button
                    variant="outlined"
                    onClick={downloadReport}
                    className="download-report-btn"
                  >
                    Download Error Report CSV
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>
        )}

        {validationResult &&
          validationResult.sampleFailures &&
          validationResult.sampleFailures.length > 0 && (
            <Grid item xs={12}>
              <Paper className="failures-table-card" elevation={2}>
                <Typography variant="h6" className="card-section-title">
                  Sample Validation Failures (Top{' '}
                  {validationResult.sampleFailures.length}
                  {validationResult.failCount > 10
                    ? ` of ${validationResult.failCount}`
                    : ''}
                  )
                </Typography>
                <TableContainer className="failures-table-container">
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell className="table-header-cell">Row</TableCell>
                        <TableCell className="table-header-cell">
                          Employee Code
                        </TableCell>
                        <TableCell className="table-header-cell">Email</TableCell>
                        <TableCell className="table-header-cell">
                          Errors / Details
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validationResult.sampleFailures.map((row) => (
                        <TableRow
                          key={row.rowNumber}
                          hover
                          className="table-body-row"
                        >
                          <TableCell className="table-body-cell bold">
                            {row.rowNumber}
                          </TableCell>
                          <TableCell className="table-body-cell">
                            {row.employeeCode ?? '-'}
                          </TableCell>
                          <TableCell className="table-body-cell">
                            {row.email || '-'}
                          </TableCell>
                          <TableCell className="table-body-cell error-text">
                            {row.comments}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}
      </Grid>
    </Box>
  );
}
